import { useEffect, useRef } from "preact/hooks";

interface UseReviewImpressionOptions {
  /** Fired once, the first time the element is at least `threshold` visible. */
  onImpression: () => void;
  /** Skip observing while false (e.g. the review has no id). */
  enabled?: boolean;
  /** Fraction of the element that must be visible to count as "seen". */
  threshold?: number;
  rootMargin?: string;
}

/**
 * Reports the first real viewport impression of an element, then stops
 * observing. Attach the returned ref to a review card's root; `onImpression`
 * fires once when ~half the card is on screen. Distinct from
 * `useIntersectionObserver` (the infinite-scroll sentinel), which fires
 * repeatedly — an impression should be counted exactly once.
 */
export function useReviewImpression({
  onImpression,
  enabled = true,
  threshold = 0.5,
  rootMargin = "0px",
}: UseReviewImpressionOptions) {
  const targetRef = useRef<HTMLDivElement | null>(null);
  const onImpressionRef = useRef(onImpression);
  const firedRef = useRef(false);

  // Keep the latest callback without re-subscribing the observer each render.
  useEffect(() => {
    onImpressionRef.current = onImpression;
  });

  useEffect(() => {
    if (!enabled || firedRef.current) return;
    const node = targetRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !firedRef.current) {
            firedRef.current = true;
            onImpressionRef.current();
            observer.disconnect(); // fire once, then stop
          }
        }
      },
      // Viewport root works for both surfaces (carousel on-page, sheet fills
      // the viewport) and observes correctly across the shadow boundary.
      { root: null, rootMargin, threshold },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, rootMargin, threshold]);

  return targetRef;
}
